import React, {
  useEffect, useMemo, useRef, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { BridgeClient } from '../../../app/service/bridge/BridgeClient';
import { loadShellSession, clearShellSession } from '../session/shellSessionStorage';
import { createGatewayClientFromShellSession } from '../session/createGatewayClientFromShellSession';
import { withAuthFailureInterceptor } from './withAuthFailureInterceptor';
import { ShellAppContext } from './ShellAppContext';
import {
  ESSENTIAL_TELEMETRY_POLICY,
  loadAuthoritativeTelemetryPolicy,
  withdrawAuthoritativeTelemetryPolicy,
} from '../../telemetry/telemetryPolicy';
import { telemetryClient } from '../../telemetry/telemetryClient';
import { shellIncidentReporter } from '../logging/shellIncidentReporter';
import TawkHelper from '../../../util/tawkHelper';
import { createProductRuntime } from '../../../app/runtime/createProductRuntime';
import { AgentUiJobWorker } from '../../agent-ui-control-plane/AgentUiJobWorker';

const TELEMETRY_POLICY_REFRESH_MS = 60000;

/**
 * ShellAppContextProvider — creates one BridgeClient and one gateway client for
 * the entire shell lifecycle and exposes them (plus session fields) via context.
 *
 * Placement: wrap AppLayout (or the outermost shell-owned component) so all
 * pages under /app/:shopId/* share a single client instance.
 *
 * In non-iframe / non-shell mode the session will be absent and `gatewayClient`
 * will be null — pages handle this via their degraded-UI path.
 *
 * Auth failure recovery:
 *   When any gateway API call throws a 401 or recognised auth error, the
 *   `withAuthFailureInterceptor`-wrapped client calls `onAuthFailure()`, which
 *   clears the shell session.
 *   - In iframe mode:  silent rebootstrap via /embedded/app?returnTo=<currentPath>
 *     so the operator is returned to their intended route automatically.
 *   - In standalone/non-iframe mode:  session is cleared and the component
 *     unmounts children (returns null).  No redirect to the embedded bootstrap
 *     lane is issued because standalone operation does not use the embedded path.
 */
export function ShellAppContextProvider({ children, cloudClient = null, productId = 'cloud' }) {
  // BridgeClient belongs exclusively to the embedded plugin composition.
  // Standalone cloud must not create or expose a dormant bridge transport.
  const bridgeRef = useRef(null);
  if (productId === 'plugin' && !bridgeRef.current) {
    bridgeRef.current = new BridgeClient();
  }

  const [redirecting, setRedirecting] = useState(false);
  const sessionRef = useRef(undefined);
  if (sessionRef.current === undefined) {
    // A gateway session belongs to the embedded plugin product. Standalone
    // cloud must ignore stale sessionStorage left by a previous iframe visit.
    sessionRef.current = productId === 'plugin' ? loadShellSession() : null;
  }
  const session = sessionRef.current;
  const [telemetryState, setTelemetryState] = useState({
    resolved: false,
    policy: ESSENTIAL_TELEMETRY_POLICY,
  });
  const telemetryWithdrawalRef = useRef(async () => false);
  const withdrawTelemetry = useCallback(
    () => telemetryWithdrawalRef.current(),
    [],
  );

  // onAuthFailure is stable (useCallback with empty deps) — safe to include in
  // the useMemo dependency array without causing unnecessary recomputes.
  const onAuthFailure = useCallback(() => {
    telemetryClient.reset();
    TawkHelper.applyTelemetryPolicy(ESSENTIAL_TELEMETRY_POLICY);
    clearShellSession();
    setRedirecting(true);
    if (productId === 'plugin') {
      // In iframe mode: silent rebootstrap so the operator returns to their
      // current route without seeing a manual-retry wall.
      const returnTo = window.location.pathname + window.location.search;
      window.location.replace(
        `/embedded/app?returnTo=${encodeURIComponent(returnTo)}`,
      );
    }
    // In standalone/non-iframe mode: session is cleared and children unmount
    // (redirecting=true returns null above).  No embedded-lane redirect issued.
  }, [productId]);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer = null;
    let activePolicy = ESSENTIAL_TELEMETRY_POLICY;
    let activeRefreshController = null;
    let policyGeneration = 0;
    let policyRequestId = 0;
    let adminLoadedDemoSessionId = null;

    // The authoritative request itself uses the bridge, so prepare only the
    // fail-closed operational channel before resolving the final policy.
    telemetryClient.configure({
      policy: ESSENTIAL_TELEMETRY_POLICY,
      session,
      appVersion: typeof window !== 'undefined' ? window.APP_VERSION : null,
      moduleVersion: session?.moduleVersion ?? null,
    });
    shellIncidentReporter.setTransport(
      incident => telemetryClient.captureOperationalIncident(incident),
    );

    const applyPolicy = (policy) => {
      if (cancelled) return;
      const changed = policy.mode !== activePolicy.mode
        || policy.policyVersion !== activePolicy.policyVersion
        || policy.expiresAt !== activePolicy.expiresAt
        || policy.demoSessionId !== activePolicy.demoSessionId;
      if (changed) {
        telemetryClient.configure({
          policy,
          session,
          appVersion: typeof window !== 'undefined' ? window.APP_VERSION : null,
          moduleVersion: session?.moduleVersion ?? null,
        });
        activePolicy = policy;
      }
      setTelemetryState({ resolved: true, policy });
      TawkHelper.applyTelemetryPolicy(policy);
      if (policy.mode === 'demo_full'
        && policy.demoSessionId
        && policy.demoSessionId !== adminLoadedDemoSessionId
      ) {
        adminLoadedDemoSessionId = policy.demoSessionId;
        telemetryClient.capture('demo_admin_loaded', { screen_name: 'admin_shell' });
      }
    };

    const resolvePolicy = async () => {
      const requestGeneration = policyGeneration;
      const requestId = ++policyRequestId;
      if (activeRefreshController) activeRefreshController.abort();
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      activeRefreshController = controller;
      const policy = await loadAuthoritativeTelemetryPolicy({
        bridge: bridgeRef.current,
        session,
        signal: controller ? controller.signal : undefined,
      });
      if (cancelled
        || requestGeneration !== policyGeneration
        || requestId !== policyRequestId
        || (controller && controller.signal.aborted)
      ) {
        return;
      }
      activeRefreshController = null;
      applyPolicy(policy);
    };

    resolvePolicy();
    refreshTimer = setInterval(resolvePolicy, TELEMETRY_POLICY_REFRESH_MS);

    const handleTelemetryWithdrawal = async () => {
      policyGeneration += 1;
      policyRequestId += 1;
      if (activeRefreshController) activeRefreshController.abort();
      activeRefreshController = null;
      applyPolicy(ESSENTIAL_TELEMETRY_POLICY);
      const withdrawn = await withdrawAuthoritativeTelemetryPolicy({ session });
      return withdrawn;
    };
    telemetryWithdrawalRef.current = handleTelemetryWithdrawal;
    window.addEventListener('kea:analytics-withdraw', handleTelemetryWithdrawal);

    const handlePageHide = () => {
      telemetryClient.endDemoSession();
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      cancelled = true;
      if (refreshTimer) clearInterval(refreshTimer);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('kea:analytics-withdraw', handleTelemetryWithdrawal);
      policyGeneration += 1;
      policyRequestId += 1;
      if (activeRefreshController) activeRefreshController.abort();
      telemetryWithdrawalRef.current = async () => false;
      shellIncidentReporter.setTransport(null);
      telemetryClient.reset();
    };
  }, [session]);

  // Transports are stable across telemetry policy refreshes. Editors must not
  // reload settings merely because observability state changed.
  const transportContext = useMemo(() => {
    // Single session read — all context fields and the gateway client derive
    // from this one snapshot so they are always consistent with each other.
    const rawGatewayClient = productId === 'plugin'
      ? createGatewayClientFromShellSession(session)
      : null;
    // Wrap the gateway client so any 401 response triggers automatic recovery.
    const gatewayClient = withAuthFailureInterceptor(rawGatewayClient, onAuthFailure);
    const productRuntime = createProductRuntime({
      productId,
      gatewayClient,
      bridgeClient: productId === 'plugin' ? bridgeRef.current : null,
      webserviceClient: cloudClient,
      capabilities: session?.capabilities ?? {},
      platform: session?.platform ?? null,
      moduleVersion: session?.moduleVersion ?? null,
    });

    return {
      gatewayClient,
      ...productRuntime,
    };
  }, [cloudClient, onAuthFailure, productId, session]);

  const contextValue = useMemo(() => {
    return {
      routeShopId: session?.routeShopId ?? null,
      gatewayToken: session?.gatewayToken ?? null,
      prestashopShopId: session?.prestashopShopId ?? null,
      adminLanguageId: session?.adminLanguageId ?? null,
      domain: session?.domain ?? null,
      storefrontUrl: session?.storefrontUrl ?? null,
      moduleVersion: session?.moduleVersion ?? null,
      gatewayAccessToken: session?.gatewayAccessToken ?? null,
      bridgeClient: productId === 'plugin' ? bridgeRef.current : null,
      ...transportContext,
      onAuthFailure,
      onboardingWizardCompleted: session?.onboardingWizardCompleted ?? null,
      wizardBypass: session?.wizardBypass ?? null,
      flowId: session?.flowId ?? null,
      telemetryPolicy: telemetryState.policy,
      telemetryPolicyResolved: telemetryState.resolved,
      withdrawTelemetry,
    };
  }, [onAuthFailure, session, telemetryState, transportContext, withdrawTelemetry]);

  // Stop rendering children while a rebootstrap navigation is in progress.
  // window.location.replace is asynchronous from React's point of view;
  // returning null prevents children from doing API work before the page unloads.
  if (redirecting) return null;

  return (
    <ShellAppContext.Provider value={contextValue}>
      <AgentUiJobWorker context={contextValue} />
      {children}
    </ShellAppContext.Provider>
  );
}

ShellAppContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  cloudClient: PropTypes.shape({
    prepareRequest: PropTypes.func,
  }),
  productId: PropTypes.oneOf(['cloud', 'plugin']),
};
